import {IExpiredActionInsertionForm, IExpiredPracticeDetailForm, IExpiredRecoveryEntrustmentForm, IExpiredThresholdForm, IExpiredWarningLetterForm, IRecoveryCompanyForm, ISearchExpiredPracticesForm} from "./models/models";
import {ChipProps, Chip, IconButton, } from "@mui/material";
import {DescriptionRounded} from "@mui/icons-material";
import {Link} from "react-router-dom";
import * as yup from "yup";
import dayjs from "dayjs";
import {ColDef, ICellRendererParams, SideBarDef, ValueFormatterParams} from "ag-grid-community";

export const LANGUAGES = ['it', 'en', 'fr'];

export const TEXT_MAX_CHAR = 3000;
export const MAIL_MAX_CHAR = 5000;
export const SMS_MAX_CHAR = 160;

export const DECIMAL_REGEXP = RegExp(/^\s*[0-9]\d*(\.\d{0,2})?\s*$/);

export const MULTIPLE_AUTOCOMPLETE_SEARCH_FILTER_KEYS = [
	"statuses",
	"substatuses",
	"products",
	"rates",
	"effectiveCheckOutLocation",
	"expectedCheckInLocation"
];

export const BOOLEAN_SEARCH_FILTER_KEYS = [
	"hideClosed",
	"hideClosedLawsuit"
];

export const AMOUNT_SEARCH_FILTER_KEYS = [
	"competanceAmountFrom", 
	"competanceAmountTo"
];

export const DATE_FORMAT_FILTER_KEYS = [
	"createdDatetimeFrom", 
	"createdDatetimeTo"
]

export const OFFSET_DATE_TIME_FILTERS_KEYS = [
	"effectiveCheckOutDateFrom", 
	"effectiveCheckOutDateTo", 
	"expectedCheckInFrom", 
	"expectedCheckInTo"
]

export enum LOCALES {
	it = "it-IT",
	en = "en-IN",
	fr = "fr-FR"
}

export enum PracticeStatus {
	new = 'NUOVO',
	sms1 = 'SMS_1',
	sms2 = 'SMS_2',
	sms3 = 'SMS_3',
	warning = 'DIFFIDA',
	trust = 'AFFIDO_SOC_ESTERNA',
	toLawsuit = 'DA_QUERELARE',
	closedToLawsuit = 'CHIUSO_PER_QUERELA',
	closed = 'CHIUSO'
}

export enum FileType {
	XLSX = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
}

export const EXPIRED_PRACTICES_INITIAL_VALUE: ISearchExpiredPracticesForm = {
	selected: false,
	expiredCode: '',
	rentalType: null,
	statuses: [],
	substatuses: [],
	raCode: '',
	bookingCode: '',
	segments: [],
    categories: [],
    products: [],
    rates: [],
	effectiveAcrissList: [],
	billingAcrissList: [],
	vehicleModels: [],
	plate: '',
	effectiveCheckOutDateFrom: null,
	effectiveCheckOutDateTo: null,
	effectiveCheckOutLocation: [],
	expectedCheckInFrom: null,
	expectedCheckInTo: null,
	expectedCheckInLocation: [],
	companyName: '',
	customerFirstName: '',
	customerLastName: '',
	customerEmail: '',
	customerMobile: '',
	customerSapId: '',
	customerFiscalCode: '',
	createdBy: '',
	createdDatetimeFrom: null,
	createdDatetimeTo: null,
	recoveryCompany: [],
	lastAction: null,
	hideClosed: true,
    hideClosedLawsuit: true,
	delaydaysFrom: 0,
	delaydaysTo: 0,
	competanceAmountFrom: '',
	competanceAmountTo: '',
	tenants: [],
	// dealers: [],
}

export const EXPIRED_THRESHOLD_INITIAL_VALUE: IExpiredThresholdForm = {
	tenant: '',
	tolleranceThreshold: {
		shortTerm: 0,
		subscription: 0,
		corporate: 0
	},
	firstSMSThreshold: {
		shortTerm: 0,
		subscription: 0,
		corporate: 0
	},
	recoveryCompanyThreshold: {
		shortTerm: 0,
		subscription: 0,
		corporate: 0
	},
	warningLetterThreshold: {
		shortTerm: 0,
		subscription: 0,
		corporate: 0
	},
	complaintThreshold: {
		shortTerm: 0,
		subscription: 0,
		corporate: 0
	},
	recoveryCompanies: []
}

export const EXPIRED_ACTION_INSERTION_INITIAL_VALUE : IExpiredActionInsertionForm = {
	operator: '',
    body: '',
	code: '',
	language: '',
	subject: '',
	attachment: [],
    raDetails: {
        raNumber: '',
        raDate: null,
        acriss: '',
        plateNumber: '',
        customerNameSurname: '',
        companyName: '',
        clientId: '',
        delayDays: '',
		phoneNumber: '',
    	rentalType: '',
    },
    returnDetails: {
        location: '',
        date: null
    }
}

export const EXPIRED_RECOVERY_ENTRUSTMENT_INITIAL_VALUE: IExpiredRecoveryEntrustmentForm = {
	recoveryCompany: null,
	// phoneNumber: '',
	// from: '',
	to: '',
	object: '',
	message: '',
	rentalType: '',
	attachmentList: []
}

/** AG Grid Config */
export const GRID_LIST_PAGE_SIZE = 25;

export const EXPIRED_DEFAULT_COL_DEF: ColDef = {
	resizable: true,
	lockPinned: true,
	unSortIcon: true,
	suppressSizeToFit: true,
};

export const EXPIRED_SIDE_BAR: SideBarDef = {
	toolPanels: [
		{
			id: 'columns',
			labelDefault: 'Columns',
			labelKey: 'columns',
			iconKey: 'columns',
			toolPanel: 'agColumnsToolPanel',
			toolPanelParams: {
				suppressRowGroups: true,
				suppressValues: true,
				suppressPivots: true,
				suppressPivotMode: true,
				suppressColumnExpandAll: true,
			},
		},
	],
	defaultToolPanel: '',
	position: 'right'
};

export function getExpiredPracticesListColmnDef(t: Function): ColDef[]  {
	return [
		{
			colId: 'EXPIREDCODE',
			field: 'expiredCode',
			checkboxSelection: true,
			headerName: t('searchExpired.form.expiredCode'),
			pinned: 'left',
			sortable: true
		},
		{
			colId: 'RENTALTYPE',
			field: 'rentalType',
			headerName: t('searchExpired.form.rentalType'),
			pinned: 'left',
			sortable: true
		},
		{
			colId: 'CATEGORY',
			field: 'category',
			headerName: t('searchExpired.form.category'),
			pinned: 'left',
			sortable: true
		},
		{
			colId: 'CREATIONDATE',
			field: 'createdDatetime',
			headerName: t('searchExpired.form.createdDatetime'),
			pinned: 'left',
			valueFormatter: (params: ValueFormatterParams<any, string>) => (params.value ? dayjs(params.value).format('DD/MM/YYYY HH:mm') : ''),
			sortable: true
		},
		{
			colId: 'STATUS',
			field: 'status',
			headerName: t('searchExpired.form.status'),
			sortable: true,
			resizable: false,
			maxWidth: 140,
			minWidth: 140,
			cellStyle: {
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center'
			},
			cellRenderer: (params: ICellRendererParams<any>) => {
				let label = undefined;
				let color: ChipProps['color'] | undefined = undefined;

				switch (params.value) {
					case PracticeStatus.new:
						label = t('common.practiceStatus.NUOVO');
						color = "primary";
						break;
					case PracticeStatus.sms1:
						label = t('common.practiceStatus.SMS_1');
						color = "warning";
						break;
					case PracticeStatus.sms2:
						label = t('common.practiceStatus.SMS_2');
						color = "warning";
						break;
					case PracticeStatus.sms3:
						label = t('common.practiceStatus.SMS_3');
						color = "warning";
						break;
					case PracticeStatus.warning:
						label = t('common.practiceStatus.DIFFIDA');
						color = "accent";
						break;
					case PracticeStatus.trust:
						label = t('common.practiceStatus.AFFIDO_SOC_ESTERNA');
						color = "error";
						break;
					case PracticeStatus.toLawsuit:
						label = t('common.practiceStatus.DA_QUERELARE');
						color = "error";
						break;
					case PracticeStatus.closedToLawsuit:
						label = t('common.practiceStatus.CHIUSO_PER_QUERELA');
						color = "warning";
						break;
					case PracticeStatus.closed:
						label = t('common.practiceStatus.CHIUSO');
						color = "success";
						break;
					default:
						return undefined;
				}
				return (<Chip label={label} color={color} />);
			},
		},
		{
			colId: 'RACODE',
			field: 'raCode',
			headerName: t('searchExpired.form.raCode'),
			sortable: true
		},
		{
			colId: 'CUSTOMERID',
			field: 'customerSapId',
			headerName: t('searchExpired.form.customerSapId'),
			sortable: true
		},
		{
			colId: 'CUSTOMERFISCALCODE',
			field: 'customerFiscalCode',
			headerName: t('searchExpired.form.customerFiscalCode'),
			sortable: true
		},
		{
			colId: 'DELAYDAYS',
			field: 'delayDays',
			headerName: t('searchExpired.form.delayDays'),
			sortable: true
		},
		{
			colId: 'COMPETANCE',
			field: 'competanceAmount',
			headerName: t('searchExpired.form.competenceAmount'),
			valueFormatter: (params: ValueFormatterParams<any, number>) => (params.value ? (params.value / 100).toFixed(2) : ''),
			sortable: true
		},
		{
			colId: 'ACRISS',
			field: 'effectiveAcriss',
			headerName: t('searchExpired.form.effectiveAcrissList'),
			sortable: true
		},
		{
			colId: 'VEHICLEMODEL',
			field: 'vehicleModel',
			headerName: t('searchExpired.form.vehicleModel'),
			sortable: true
		},
		{
			colId: 'PLATE',
			field: 'plate',
			headerName: t('searchExpired.form.plate'),
			sortable: true
		},
		{
			colId: 'EXPECTEDCHECKINDATE',
			field: 'expectedCheckInDate',
			headerName: t('searchExpired.form.expectedCheckinDate'),
			valueFormatter: (params: ValueFormatterParams<any, string>) => (params.value ? dayjs(params.value).format('DD/MM/YYYY HH:mm') : ''),
			sortable: true
		},
		{
			colId: 'CUSTOMERNAME',
			field: 'customer',
			headerName: t('searchExpired.form.customer'),
			sortable: true,
			cellRenderer: (params: ICellRendererParams<any>) => {
				return params?.data?.companyName ?? (params?.data?.customerLastName || '') + ' ' + (params?.data?.customerFirstName || '');
			}
		},
		{
			colId: 'RECOVERYCOMPANY',
			field: 'recoveryCompany',
			headerName: t('searchExpired.form.recoveryCompany'),
			sortable: true,
			cellRenderer: (params: ICellRendererParams<any>) => {
				return params?.data?.recoveryCompany?.name || '';
			}
		},
		{
			colId: 'LASTACTION',
			field: 'lastAction',
			headerName: t('searchExpired.form.lastAction'),
			valueFormatter: (params: ValueFormatterParams<any, string>) => (params.value ? t(`common.actions.${params.value}`, params.value) : ''),
			sortable: true
		},
		{
			field: '_actions',
			headerName: '',
			minWidth: 70,
			resizable: false,
			suppressMovable: true,
			suppressSizeToFit: false,
			suppressColumnsToolPanel: true,
			pinned: 'right',
			cellRenderer: (params: ICellRendererParams<any>) => {
				return (
					<Link to={`/expired-management/practice-detail/${params.data?.expiredCode}`} target="_blank">
						<IconButton title={t('common.button.detail')} color="accent" >
							<DescriptionRounded />
						</IconButton>
					</Link>
				);
			}
		}
	]
}

export function getExpiredPracticeDetailActionHistoryListColmnDef(t: Function, viewActionDetail: Function): ColDef[]  {
	return [
		{
			headerName: t('#'),
			valueGetter: "node.rowIndex + 1",
			pinned: 'left'
		},
		{
			field: 'createdDatetime',
			headerName: t('actionHistory.form.creationDatetime'),
			valueFormatter: (params: ValueFormatterParams<any, string>) => (params.value ? dayjs(params.value).format('DD/MM/YYYY HH:mm') : ''),
			sortable: false
		},
		{
			field: 'executionDate',
			headerName: t('actionHistory.form.executionDate'),
			valueFormatter: (params: ValueFormatterParams<any, string>) => (params.value ? dayjs(params.value).format('DD/MM/YYYY HH:mm') : ''),
			sortable: false
		},
		{
			field: 'updatedDatetime',
			headerName: t('actionHistory.form.updatedDatetime'),
			valueFormatter: (params: ValueFormatterParams<any, string>) => (params.value ? dayjs(params.value).format('DD/MM/YYYY HH:mm') : ''),
			sortable: false
		},
		{
			field: 'creationUser',
			headerName: t('actionHistory.form.creationUser'),
			sortable: false
		},
		{
			field: 'action',
			headerName: t('actionHistory.form.action'),
			valueFormatter: (params: ValueFormatterParams<any, string>) => (params.value ? t(`common.actions.${params.value}`, params.value) : ''),
			sortable: false
		},
		{
			field: 'recoveryCompany',
			headerName: t('actionHistory.form.recoveryCompany'),
			sortable: false,
			cellRenderer: (params: ICellRendererParams<any>) => {
				return params?.data?.recoveryCompany?.name || '';
			}
		},
		{
			field: 'note',
			headerName: t('actionHistory.form.note'),
			sortable: false
		},
		{
			field: '_actions',
			headerName: '',
			maxWidth: 70,
            minWidth: 70,
			resizable: false,
			suppressMovable: true,
			suppressSizeToFit: false,
			suppressColumnsToolPanel: true,
			pinned: 'right',
			cellRenderer: (params: ICellRendererParams<any>) => {
				return (
					<IconButton title={t('common.button.detail')} color="accent" onClick={(e) => viewActionDetail(e, params.data)} >
						<DescriptionRounded />
					</IconButton>
				);
			}
		}
	]
}

/* Yup Validation Schema */

export const SEARCH_EXPIRED_PRACTICES_FORM_VALIDATION_SCHEMA = yup.object()
	.shape({
		createdDatetimeFrom: yup
            .date()
            .typeError("common.message.validation.invalidDate")
            .nullable()
            .test(
                "testCreatedDateFrom",
                "common.message.validation.rangeNotValid",
                (value, context) => {
                    return (
                        !value ||
                        !context.parent.createdDatetimeTo ||
                        value <= context.parent.createdDatetimeTo
                    );
                }
            ),
			createdDatetimeTo: yup
            .date()
            .typeError("common.message.validation.invalidDate")
            .nullable()
            .test(
                "testCreatedDateTo",
                "common.message.validation.rangeNotValid",
                (value, context) => {
                    return (
                        !value ||
                        !context.parent.createdDatetimeFrom ||
                        value >= context.parent.createdDatetimeFrom
                    );
                }
            ),
			effectiveCheckOutDateFrom: yup
            .date()
            .typeError("common.message.validation.invalidDate")
            .nullable()
            .test(
                "testEffectiveCheckOutDateFrom",
                "common.message.validation.rangeNotValid",
                (value, context) => {
                    return (
                        !value ||
                        !context.parent.effectiveCheckOutDateTo ||
                        value <= context.parent.effectiveCheckOutDateTo
                    );
                }
            ),
			effectiveCheckOutDateTo: yup
            .date()
            .typeError("common.message.validation.invalidDate")
            .nullable()
            .test(
                "testEffectiveCheckOutDateTo",
                "common.message.validation.rangeNotValid",
                (value, context) => {
                    return (
                        !value ||
                        !context.parent.effectiveCheckOutDateFrom ||
                        value >= context.parent.effectiveCheckOutDateFrom
                    );
                }
            ),
			expectedCheckInFrom: yup
            .date()
            .typeError("common.message.validation.invalidDate")
            .nullable()
            .test(
                "testExpectedCheckInDateFrom",
                "common.message.validation.rangeNotValid",
                (value, context) => {
                    return (
                        !value ||
                        !context.parent.expectedCheckInTo ||
                        value <= context.parent.expectedCheckInTo
                    );
                }
            ),
			expectedCheckInTo: yup
            .date()
            .typeError("common.message.validation.invalidDate")
            .nullable()
            .test(
                "testExpectedCheckInDateTo",
                "common.message.validation.rangeNotValid",
                (value, context) => {
                    return (
                        !value ||
                        !context.parent.expectedCheckInFrom ||
                        value >= context.parent.expectedCheckInFrom
                    );
                }
            ),
			competanceAmountFrom: yup
			.string()
			.matches(DECIMAL_REGEXP, {excludeEmptyString: true, message: "common.message.validation.notValid"})
			.test(
                "testCompetanceAmountFrom",
                "common.message.validation.rangeNotValid",
                (value, context) => {
                    return (
                        !value ||
                        !context.parent.competanceAmountTo ||
                        value <= context.parent.competanceAmountTo
                    );
                }
            ),
			competanceAmountTo: yup
			.string()
			.matches(DECIMAL_REGEXP, {excludeEmptyString: true, message: "common.message.validation.notValid"})
			.test(
                "testCompetanceAmountTo",
                "common.message.validation.rangeNotValid",
                (value, context) => {
                    return (
                        !value ||
                        !context.parent.competanceAmountFrom ||
                        value >= context.parent.competanceAmountFrom
                    );
                }
            ),
			delaydaysFrom: yup
			.number()
			.test(
                "testDelayDaysFrom",
                "common.message.validation.rangeNotValid",
                (value, context) => {
                    return (
                        !value ||
                        !context.parent.delaydaysTo ||
                        value <= context.parent.delaydaysTo
                    );
                }
            ),
			delaydaysTo: yup
			.number()
			.test(
                "testDelayDaysTo",
                "common.message.validation.rangeNotValid",
                (value, context) => {
                    return (
                        !value ||
                        !context.parent.delaydaysFrom ||
                        value >= context.parent.delaydaysFrom
                    );
                }
            ),
		tenants: yup.array(yup.string()),
		dealers: yup.array(yup.string()),

			
	});

export const EXPIRED_MANAGEMENT_CONFIGURATION_FORM_VALIDATION_SCHEMA = yup.object()
	.shape({
		recoveryCompanies: yup.array().of(
			yup.object().shape({
				amount: yup.string().matches(DECIMAL_REGEXP, {excludeEmptyString: true, message: "common.message.validation.notValid"}),
		}
		))
	});

export const EXPIRED_ACTION_INSERTION_FORM_VALIDATION_SCHEMA = yup.object()
.shape({
	body: yup
	.string()
	.min(1, "common.message.validation.required")
	.test(
		"testBodyMaxChar", 
		"common.message.validation.tooMuchChars", 
		(value, context) => {
			return context.options?.context?.bodyMaxCharTestRequired && !!value ? !!context.options?.context?.bodyMaxChar && (value.length <= context.options.context.bodyMaxChar) : true;
		}
	),
});

export const EXPIRED_RECOVERY_ENTRUSTMENT_FORM_VALIDATION_SCHEMA = yup.object()
.shape({
	recoveryCompany: yup.object().nullable().required("common.message.validation.required"),
	to: yup.string().required("common.message.validation.required")
});

export const EXPIRED_PRACTICES_DETAIL_FORM_VALIDATION_SCHEMA = yup.object()
	.shape({
		thresholds: yup.object({
			firstSmsExpectedDate: yup
				.date()
				.typeError("common.message.validation.invalidDate")
				.nullable()
				.required("common.message.validation.required"),

			recoverySocietyExpectedDate: yup
				.date()
				.typeError("common.message.validation.invalidDate")
				.nullable()
				.required("common.message.validation.required"),

			formalNoticeExpectedDate: yup
				.date()
				.typeError("common.message.validation.invalidDate")
				.nullable()
				.required("common.message.validation.required"),

			lawSuitExpectedDate: yup
				.date()
				.typeError("common.message.validation.invalidDate")
				.nullable()
				//.required("common.message.validation.required"),
		})
	});

export const EXPIRED_PRACTICE_RECOVERY_COMPANY_FORM_VALIDATION_SCHEMA = yup.object()
.shape({
	recoveryCompany: yup.object().nullable().required("common.message.validation.required")
});
/* Enums */

export enum EXPIRED_PRACTICES_QUERY_KEY {
	DOMAINS = 'EXPIRED_PRACTICES_DOMAINS',
	FIND_EXPIRED_CONFIGURATION = "FIND_EXPIRED_CONFIGURATION",
	UPDATE_EXPIRED_CONFIGURATION = "UPDATE_EXPIRED_CONFIGURATION",
	GET_ACTION_MESSAGE_TEMPLATE = "GET_ACTION_MESSAGE_TEMPLATE",
	EXECUTE_ACTION = "EXECUTE_ACTION",
	FIND_RECOVERY_COMPANIES = "FIND_RECOVERY_COMPANIES",
	GET_AVAIABLE_EXPIRED_ACTIONS = "GET_AVAIABLE_EXPIRE_ACTIONS",
	GET_AVAIABLE_EXPIRED_ACTIONS_FOR_VIEW = "GET_AVAIABLE_EXPIRE_ACTIONS_FOR_VIEW",
	FETCH_EXPIRED_PRACTICE_ACTION_HISTORY = "FETCH_EXPIRED_PRACTICE_ACTION_HISTORY",
	FETCH_EXPIRED_PRACTICE = "FETCH_EXPIRED_PRACTICE",
	GET_EXPIRED_PRACTICE_SUMMARY = "GET_EXPIRED_PRACTICE_SUMMARY",
	GET_EXPIRED_FROM_RA_CODE = "GET_EXPIRED_FROM_RA_CODE",
}

export enum ExpiredInsertionAction {
	SMS1 = "SMS_1",
	SMS2 = "SMS_2",
	SMS3 = "SMS_3",
	NEW_SMS = "NUOVO_SMS",
	RECORD_CALL = "TELEFONATA",
	OPERATOR_NOTES = "NOTE_OPERATORE",
	IN_DMS = "VEICOLO_IN_DMS",
	IN_CAR_REPAIR_SHOP = "VEICOLO_IN_OFFICINA",
	NO_VEHICLE = "NO_VEICOLO",
	MISAPPROPRIATION = "APPROPRIAZIONE_INDEBITA",
	THEFT = "FURTO",
	POSITIVE_RECOVERY = "RECUPERO_OK",
	NEGATIVE_RECOVERY = "RECUPERO_KO",
	WARNING_LETTER = "DIFFIDA",
	RECOVERY = "RECUPERATORE"
}

/* Detail */
export const EXPIRED_PRACTICE_DETAIL_INITIAL_VALUE: IExpiredPracticeDetailForm = {
	detail: {
		expiredCode: '',
		status: '',
		watchlist: false,
		rentalType: '',
		createdDatetime: null,

		competanceAmount: '',
        delayDays: '',
		raCode: '',
		bookingCode: '',

		segment: '',
		category: '',
		product: '',
		rate: '',

		billingAcriss: '',
		effectiveAcriss: '',
		vehicleModel: '',
		plate: '',

        effectiveCheckOutDate: null,
        effectiveCheckOutLocationLabel: '',
        expectedCheckInDate: null,
        expectedeCheckInLocationLabel: '',

        customerSapId: '',
        customerNameSurname: '',
        customerEmail: '',
        customerMobile: '',

		companySapId: '',
		companyName: '',
		companyEmail: '',
		companyMobile: '',

        recoveryCompany: '',
		recoveryCompanyEmail: ''
	},
	attachemets: {

	},
	thresholds: {
		firstSmsExpectedDate: null,
		recoverySocietyExpectedDate: null,
		formalNoticeExpectedDate: null,
		lawSuitExpectedDate: null,
		autoSms: false
	}
}

export const EXPIRED_RECOVERY_COMPANY_FORM_INITIAL_VALUE: IRecoveryCompanyForm = {
	recoveryCompany: null
}

export const ACTION_DETAIL_INITIAL_VALUE = {
	createdDatetime: null,
	executionDate: null,
	updatedDatetime: null,
	creationUser: "",
	action: "",
	recoveryCompany: "",
	body: ""
}

export const EXPIRED_WARNING_LETTER_FORM_INITIAL_VALUE: IExpiredWarningLetterForm = {
	companyName: '',
	customerEmail: '',
	customerNameSurname: '',
	customerSapId: '',
	file: null
}